import { useState, useEffect } from "react";
import { api } from "../lib/utils";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Checkbox } from "./ui/checkbox";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Briefcase,
  GraduationCap,
  History,
  ShieldCheck,
  Loader2,
  CheckCircle,
  Plus,
  Trash2,
  ChevronRight,
  AlertCircle,
  User,
} from "lucide-react";

const TABS = [
  { id: "personal", label: "Personal", icon: User },
  { id: "employment", label: "Employment", icon: Briefcase },
  { id: "training", label: "Training", icon: GraduationCap },
  { id: "history", label: "Work History", icon: History },
  { id: "declaration", label: "Declaration", icon: ShieldCheck },
];

export default function ProfileCompletionForm({ candidate, onComplete }) {
  const [activeTab, setActiveTab] = useState("personal");
  const [saving, setSaving] = useState(false);

  // Personal fields
  const [fullName, setFullName] = useState(candidate.full_name || "");
  const [phone, setPhone] = useState(candidate.phone || "");
  const [nationality, setNationality] = useState(candidate.nationality || "");
  const [address, setAddress] = useState(candidate.address || "");
  const [dateOfBirth, setDateOfBirth] = useState(candidate.date_of_birth || "");

  // Employment fields
  const [currentEmployer, setCurrentEmployer] = useState(candidate.current_employer || "");
  const [currentRole, setCurrentRole] = useState(candidate.current_role || "");
  const [yearsExperience, setYearsExperience] = useState(candidate.years_experience ?? "");
  const [keyResponsibilities, setKeyResponsibilities] = useState(candidate.key_responsibilities || "");
  const [selfRating, setSelfRating] = useState(candidate.self_rating ?? "");
  const [selfRatingJustification, setSelfRatingJustification] = useState(candidate.self_rating_justification || "");

  // Training certifications
  const [trainings, setTrainings] = useState(
    candidate.training_certifications?.length
      ? candidate.training_certifications
      : [{ name: "", date: "", issuer: "" }]
  );

  // Employment history
  const [employmentHistory, setEmploymentHistory] = useState(
    candidate.employment_history?.length
      ? candidate.employment_history
      : [{ employer: "", role: "", from_date: "", to_date: "", duties: "", employment_type: "full_time" }]
  );

  // Declaration
  const [declarationAccepted, setDeclarationAccepted] = useState(candidate.declaration_accepted || false);

  // Section completion status
  const sectionStatus = candidate.profile_section_status || {};

  const isPersonalComplete = !!(fullName && phone && nationality);
  const isEmploymentComplete = !!(currentEmployer && currentRole && yearsExperience !== "" && keyResponsibilities);
  const isTrainingComplete = trainings.some(t => t.name && t.issuer);
  const isHistoryComplete = employmentHistory.some(h => h.employer && h.role);
  const isDeclarationComplete = declarationAccepted;

  const completedCount = [isPersonalComplete, isEmploymentComplete, isTrainingComplete, isHistoryComplete, isDeclarationComplete].filter(Boolean).length;

  const handleSave = async (navigateNext = false) => {
    setSaving(true);
    try {
      const payload = {
        full_name: fullName,
        phone: phone,
        nationality: nationality,
        address: address,
        date_of_birth: dateOfBirth || null,
        current_employer: currentEmployer,
        current_role: currentRole,
        years_experience: yearsExperience !== "" ? parseInt(yearsExperience) : null,
        key_responsibilities: keyResponsibilities,
        self_rating: selfRating !== "" ? parseInt(selfRating) : null,
        self_rating_justification: selfRatingJustification,
        training_certifications: trainings.filter(t => t.name),
        employment_history: employmentHistory.filter(h => h.employer),
        declaration_accepted: declarationAccepted,
        declaration_date: declarationAccepted ? new Date().toISOString() : null,
      };

      const res = await api.put("/my-application/profile", payload);
      toast.success("Profile saved successfully");

      if (res.data.profile_completed) {
        toast.success("Profile complete! You can now upload documents.");
        onComplete();
        return;
      }

      if (navigateNext) {
        const currentIdx = TABS.findIndex(t => t.id === activeTab);
        if (currentIdx < TABS.length - 1) {
          setActiveTab(TABS[currentIdx + 1].id);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  // Training helpers
  const addTraining = () => setTrainings([...trainings, { name: "", date: "", issuer: "" }]);
  const removeTraining = (idx) => setTrainings(trainings.filter((_, i) => i !== idx));
  const updateTraining = (idx, field, value) => {
    const updated = [...trainings];
    updated[idx] = { ...updated[idx], [field]: value };
    setTrainings(updated);
  };

  // Employment history helpers
  const addHistory = () => setEmploymentHistory([
    ...employmentHistory,
    { employer: "", role: "", from_date: "", to_date: "", duties: "", employment_type: "full_time" },
  ]);
  const removeHistory = (idx) => setEmploymentHistory(employmentHistory.filter((_, i) => i !== idx));
  const updateHistory = (idx, field, value) => {
    const updated = [...employmentHistory];
    updated[idx] = { ...updated[idx], [field]: value };
    setEmploymentHistory(updated);
  };

  return (
    <Card className="border-blue-200" data-testid="profile-completion-form">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-xl">Complete Your Profile</CardTitle>
            <CardDescription className="mt-1">
              Please fill in all 5 sections before uploading documents.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-full">
            <span className="text-sm font-semibold text-blue-700" data-testid="profile-progress-count">
              {completedCount}/5
            </span>
            <span className="text-xs text-blue-500">sections</span>
          </div>
        </div>

        {/* Section progress pills */}
        <div className="flex gap-2 mt-4 flex-wrap">
          {TABS.map(tab => {
            const isComplete =
              tab.id === "personal" ? isPersonalComplete :
              tab.id === "employment" ? isEmploymentComplete :
              tab.id === "training" ? isTrainingComplete :
              tab.id === "history" ? isHistoryComplete :
              isDeclarationComplete;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`profile-pill-${tab.id}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  isComplete
                    ? "bg-green-100 text-green-700 border border-green-300"
                    : activeTab === tab.id
                    ? "bg-blue-100 text-blue-700 border border-blue-300"
                    : "bg-slate-100 text-slate-500 border border-slate-200"
                }`}
              >
                {isComplete ? <CheckCircle className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                {tab.label}
              </button>
            );
          })}
        </div>
      </CardHeader>

      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="hidden">
            {TABS.map(t => <TabsTrigger key={t.id} value={t.id}>{t.label}</TabsTrigger>)}
          </TabsList>

          {/* TAB 1: Personal */}
          <TabsContent value="personal">
            <div className="space-y-4" data-testid="profile-tab-personal">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="full_name"
                    data-testid="input-full-name"
                    value={fullName}
                    onChange={e => setFullName(e.target.value)}
                    placeholder="e.g., John Smith"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    value={candidate.email || ""}
                    disabled
                    className="bg-slate-50 text-slate-500"
                  />
                  <p className="text-xs text-slate-400">Email cannot be changed</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number <span className="text-red-500">*</span></Label>
                  <Input
                    id="phone"
                    data-testid="input-phone"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    placeholder="e.g., +60 12-345 6789"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nationality">Nationality <span className="text-red-500">*</span></Label>
                  <Input
                    id="nationality"
                    data-testid="input-nationality"
                    value={nationality}
                    onChange={e => setNationality(e.target.value)}
                    placeholder="e.g., Malaysian"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date_of_birth">Date of Birth</Label>
                  <Input
                    id="date_of_birth"
                    data-testid="input-dob"
                    type="date"
                    value={dateOfBirth}
                    onChange={e => setDateOfBirth(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    data-testid="input-address"
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                    placeholder="e.g., 123 Main Street, Kuala Lumpur"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button onClick={() => handleSave(true)} disabled={saving} data-testid="btn-save-personal">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save & Continue
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* TAB 2: Employment */}
          <TabsContent value="employment">
            <div className="space-y-4" data-testid="profile-tab-employment">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="current_employer">Current Employer <span className="text-red-500">*</span></Label>
                  <Input
                    id="current_employer"
                    data-testid="input-current-employer"
                    value={currentEmployer}
                    onChange={e => setCurrentEmployer(e.target.value)}
                    placeholder="e.g., ABC Construction Ltd"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="current_role">Current Role <span className="text-red-500">*</span></Label>
                  <Input
                    id="current_role"
                    data-testid="input-current-role"
                    value={currentRole}
                    onChange={e => setCurrentRole(e.target.value)}
                    placeholder="e.g., Site Manager"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="years_experience">Years of Experience <span className="text-red-500">*</span></Label>
                  <Input
                    id="years_experience"
                    data-testid="input-years-experience"
                    type="number"
                    min="0"
                    max="60"
                    value={yearsExperience}
                    onChange={e => setYearsExperience(e.target.value)}
                    placeholder="e.g., 10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="self_rating">Self-Assessment Rating (1-5)</Label>
                  <Select value={selfRating ? String(selfRating) : ""} onValueChange={v => setSelfRating(v)}>
                    <SelectTrigger data-testid="select-self-rating">
                      <SelectValue placeholder="Rate yourself" />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5].map(n => (
                        <SelectItem key={n} value={String(n)}>
                          {n} - {["Beginner", "Basic", "Competent", "Proficient", "Expert"][n - 1]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="key_responsibilities">Key Responsibilities <span className="text-red-500">*</span></Label>
                <Textarea
                  id="key_responsibilities"
                  data-testid="input-key-responsibilities"
                  value={keyResponsibilities}
                  onChange={e => setKeyResponsibilities(e.target.value)}
                  placeholder="Describe your main duties and responsibilities in your current role..."
                  rows={4}
                />
              </div>

              {selfRating && (
                <div className="space-y-2">
                  <Label htmlFor="self_rating_justification">Rating Justification</Label>
                  <Textarea
                    id="self_rating_justification"
                    data-testid="input-self-rating-justification"
                    value={selfRatingJustification}
                    onChange={e => setSelfRatingJustification(e.target.value)}
                    placeholder="Briefly justify your self-assessment rating..."
                    rows={2}
                  />
                </div>
              )}

              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={() => setActiveTab("personal")}>
                  Back
                </Button>
                <Button onClick={() => handleSave(true)} disabled={saving} data-testid="btn-save-employment">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save & Continue
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* TAB 3: Training & Certifications */}
          <TabsContent value="training">
            <div className="space-y-4" data-testid="profile-tab-training">
              <p className="text-sm text-slate-600">
                List relevant training courses, certifications, or qualifications you have completed.
              </p>

              {trainings.map((t, idx) => (
                <div key={idx} className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">Training #{idx + 1}</span>
                    {trainings.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTraining(idx)}
                        className="text-red-500 hover:text-red-700 h-8 px-2"
                        data-testid={`btn-remove-training-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Course / Certificate Name <span className="text-red-500">*</span></Label>
                      <Input
                        data-testid={`input-training-name-${idx}`}
                        value={t.name}
                        onChange={e => updateTraining(idx, "name", e.target.value)}
                        placeholder="e.g., OSHA Safety Cert"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Issuing Organisation <span className="text-red-500">*</span></Label>
                      <Input
                        data-testid={`input-training-issuer-${idx}`}
                        value={t.issuer}
                        onChange={e => updateTraining(idx, "issuer", e.target.value)}
                        placeholder="e.g., NISO"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Date Completed</Label>
                      <Input
                        data-testid={`input-training-date-${idx}`}
                        type="date"
                        value={t.date}
                        onChange={e => updateTraining(idx, "date", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}

              <Button variant="outline" size="sm" onClick={addTraining} data-testid="btn-add-training">
                <Plus className="w-4 h-4 mr-1" />
                Add Training
              </Button>

              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={() => setActiveTab("employment")}>
                  Back
                </Button>
                <Button onClick={() => handleSave(true)} disabled={saving} data-testid="btn-save-training">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save & Continue
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* TAB 4: Employment History */}
          <TabsContent value="history">
            <div className="space-y-4" data-testid="profile-tab-history">
              <p className="text-sm text-slate-600">
                Provide your employment history. Include all relevant positions that demonstrate your competencies.
              </p>

              {employmentHistory.map((h, idx) => (
                <div key={idx} className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">Position #{idx + 1}</span>
                    {employmentHistory.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeHistory(idx)}
                        className="text-red-500 hover:text-red-700 h-8 px-2"
                        data-testid={`btn-remove-history-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Employer <span className="text-red-500">*</span></Label>
                      <Input
                        data-testid={`input-history-employer-${idx}`}
                        value={h.employer}
                        onChange={e => updateHistory(idx, "employer", e.target.value)}
                        placeholder="e.g., XYZ Corp"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Role / Job Title <span className="text-red-500">*</span></Label>
                      <Input
                        data-testid={`input-history-role-${idx}`}
                        value={h.role}
                        onChange={e => updateHistory(idx, "role", e.target.value)}
                        placeholder="e.g., Electrician"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">From</Label>
                      <Input
                        data-testid={`input-history-from-${idx}`}
                        type="date"
                        value={h.from_date}
                        onChange={e => updateHistory(idx, "from_date", e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">To</Label>
                      <Input
                        data-testid={`input-history-to-${idx}`}
                        type="date"
                        value={h.to_date}
                        onChange={e => updateHistory(idx, "to_date", e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Type</Label>
                      <Select value={h.employment_type || "full_time"} onValueChange={v => updateHistory(idx, "employment_type", v)}>
                        <SelectTrigger data-testid={`select-history-type-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full_time">Full Time</SelectItem>
                          <SelectItem value="part_time">Part Time</SelectItem>
                          <SelectItem value="contract">Contract</SelectItem>
                          <SelectItem value="self_employed">Self Employed</SelectItem>
                          <SelectItem value="volunteer">Volunteer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Key Duties</Label>
                    <Textarea
                      data-testid={`input-history-duties-${idx}`}
                      value={h.duties}
                      onChange={e => updateHistory(idx, "duties", e.target.value)}
                      placeholder="Brief description of duties and achievements..."
                      rows={2}
                    />
                  </div>
                </div>
              ))}

              <Button variant="outline" size="sm" onClick={addHistory} data-testid="btn-add-history">
                <Plus className="w-4 h-4 mr-1" />
                Add Position
              </Button>

              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={() => setActiveTab("training")}>
                  Back
                </Button>
                <Button onClick={() => handleSave(true)} disabled={saving} data-testid="btn-save-history">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save & Continue
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* TAB 5: Declaration */}
          <TabsContent value="declaration">
            <div className="space-y-6" data-testid="profile-tab-declaration">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
                <h4 className="font-semibold text-amber-900 mb-3">Declaration of Authenticity</h4>
                <div className="space-y-3 text-sm text-amber-800">
                  <p>By accepting this declaration, I confirm that:</p>
                  <ul className="list-disc list-inside space-y-1.5 ml-2">
                    <li>All information provided in this application is true, accurate, and complete to the best of my knowledge.</li>
                    <li>All documents submitted are authentic and have not been falsified or tampered with.</li>
                    <li>I understand that providing false or misleading information may result in the rejection of my application or revocation of any award granted.</li>
                    <li>I consent to the processing of my personal data for the purposes of this RPL assessment.</li>
                    <li>I understand that my application may be subject to verification and quality assurance checks.</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-white border border-slate-200 rounded-lg">
                <Checkbox
                  id="declaration"
                  data-testid="checkbox-declaration"
                  checked={declarationAccepted}
                  onCheckedChange={setDeclarationAccepted}
                  className="mt-0.5"
                />
                <label htmlFor="declaration" className="text-sm text-slate-700 cursor-pointer leading-relaxed">
                  I have read and agree to the above declaration. I confirm that all information provided is truthful and accurate.
                </label>
              </div>

              {!isPersonalComplete || !isEmploymentComplete || !isTrainingComplete || !isHistoryComplete ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-800">Incomplete sections</p>
                    <p className="text-xs text-red-600 mt-1">
                      Please complete all previous sections before submitting:
                      {!isPersonalComplete && " Personal,"}
                      {!isEmploymentComplete && " Employment,"}
                      {!isTrainingComplete && " Training,"}
                      {!isHistoryComplete && " Work History"}
                    </p>
                  </div>
                </div>
              ) : null}

              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={() => setActiveTab("history")}>
                  Back
                </Button>
                <Button
                  onClick={() => handleSave(false)}
                  disabled={saving || !declarationAccepted || !isPersonalComplete || !isEmploymentComplete || !isTrainingComplete || !isHistoryComplete}
                  data-testid="btn-submit-profile"
                  className="bg-green-600 hover:bg-green-700"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Complete Profile & Continue
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
